// Extract plain text from a PDF in the browser using pdfjs-dist. Works on
// vector PDFs (contract docs, drawing title blocks). Scanned/raster-only PDFs
// yield little text — those would need OCR, which is out of scope here.

import * as pdfjsLib from "pdfjs-dist";
// Vite resolves this to a hashed URL for the worker bundle.
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

// Re-exported so a PDF blob can be rendered to a canvas (used for verification
// and any future in-app PDF preview).
export const getDocument = pdfjsLib.getDocument;

export interface ExtractResult {
  fileName: string;
  pages: number;
  text: string;
  chars: number;
}

export async function extractPdfText(file: File): Promise<ExtractResult> {
  return extractBufferText(new Uint8Array(await file.arrayBuffer()), file.name);
}

// Extract text from a PDF already in memory (e.g. the app's own rendered output
// PDF blob) so it can be reviewed exactly as it will be exported.
export async function extractBlobText(blob: Blob, label = "document.pdf"): Promise<ExtractResult> {
  return extractBufferText(new Uint8Array(await blob.arrayBuffer()), label);
}

async function extractBufferText(buf: Uint8Array, name: string): Promise<ExtractResult> {
  const file = { name };
  const doc = await pdfjsLib.getDocument({ data: buf }).promise;
  const parts: string[] = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    let line = "";
    let lastY: number | null = null;
    const lines: string[] = [];
    for (const item of content.items as any[]) {
      const y = item.transform?.[5];
      if (lastY !== null && y != null && Math.abs(y - lastY) > 2) {
        lines.push(line);
        line = "";
      }
      line += item.str;
      lastY = y ?? lastY;
    }
    if (line) lines.push(line);
    parts.push(`\n===== ${file.name} — page ${i} =====\n` + lines.join("\n"));
  }
  const text = parts.join("\n");
  return { fileName: file.name, pages: doc.numPages, text, chars: text.length };
}

export async function extractManyPdfs(files: File[]): Promise<ExtractResult[]> {
  const out: ExtractResult[] = [];
  for (const f of files) {
    try {
      out.push(await extractPdfText(f));
    } catch (e: any) {
      out.push({ fileName: f.name, pages: 0, text: "", chars: 0 });
    }
  }
  return out;
}
